<template>
    <ValidationObserver ref="customerLeads">
        <div v-if="isLoadingServices">
            <div class="grid__col-12 ls-margin--vertical">
                <div class="ls-align--center">
                    <span class="fa-stack fa-2x ls-color--green">
                        <i class="fa fa-stack-2x fa-circle"></i>
                        <i class="fa fa-stack-1x fa-spin fa-leaf fa-inverse"></i>
                    </span>
                </div>
                <p class="ls-align--center ls-margin--bottom" data-font-size="-1">
                    Loading Services&hellip;
                </p>
            </div>
        </div>
        <div v-if="!isLoadingServices" class="ls-customer-leads--container grid">
            <div class="grid__col-12">
                <slot></slot>
            </div>
            <div class="grid">
                <div class="grid__col-12">
                    <h2 class="ls-condensed section-title">
                        What services are you interested in?
                    </h2>
                </div>
                <div class="grid__col-12">
                    <ValidationProvider v-slot="{ classes }" ref="services" rules="required">
                        <div
                            class="ls-customer-leads--services-outer-container ls-button-picker ls-margin--top"
                        >
                            <!-- MOWING -->
                            <a
                                class="ls-button ls-button--block"
                                test-id="service-1"
                                @click.prevent="onLawnMowingClick()"
                            >
                                Lawn Mowing
                            </a>
                            <!-- TREATMENT -->
                            <a
                                v-if="isTreatmentService()"
                                class="ls-button ls-button--block"
                                :test-id="`service-2`"
                                @click.prevent="redirectToSignupWeb('treatment')"
                            >
                                Lawn Treatment
                            </a>
                            <!-- LEAF REMOVAL -->
                            <a
                                v-if="hasServices([serviceIds.LEAF_REMOVAL])"
                                class="ls-button ls-button--block"
                                :test-id="`service-10`"
                                @click.prevent="redirectToSignupWeb('leafRemoval')"
                            >
                                Leaf Removal
                            </a>
                            <!-- BUSH TRIMMING -->
                            <a
                                v-if="hasServices([serviceIds.BUSH_TRIMMING])"
                                class="ls-button ls-button--block"
                                :test-id="`service-8`"
                                @click.prevent="redirectToSignupWeb('bushTrimming')"
                            >
                                Bush Trimming
                            </a>
                            <!-- BED WEEDING -->
                            <a
                                v-if="hasServices([serviceIds.BED_WEEDING])"
                                class="ls-button ls-button--block"
                                :test-id="`service-170`"
                                @click.prevent="redirectToSignupWeb('flowerBedWeeding')"
                            >
                                Bed Weeding
                            </a>
                            <!-- MOSQUITO -->
                            <a
                                v-if="hasServices([serviceIds.MOSQUITO])"
                                class="ls-button ls-button--block"
                                :test-id="`service-4599`"
                                @click.prevent="redirectToSignupWeb('mosquito')"
                            >
                                Mosquito
                            </a>
                            <!-- AERATION -->
                            <a
                                v-if="hasServices([serviceIds.AERATION])"
                                class="ls-button ls-button--block"
                                :test-id="`service-5`"
                                @click.prevent="redirectToSignupWeb('aeration')"
                            >
                                Aeration
                            </a>
                            <!-- AERATION & OVERSEEDING -->
                            <a
                                v-if="hasServices([serviceIds.AERATION_OVERSEEDING])"
                                class="ls-button ls-button--block"
                                :test-id="`service-17`"
                                @click.prevent="redirectToSignupWeb('aerationOverseedingPackage')"
                            >
                                Aeration &amp; Overseeding
                            </a>
                            <!-- DYNAMIC SERVICES -->
                            <label
                                v-for="service in showAll
                                    ? filteredServices
                                    : filteredServices.slice(0, 9)"
                                :key="`service-${service.id}`"
                                class="ls-button ls-button--block ls-button--checkbox"
                                :class="{
                                    'ls-button--green ls-button--faded': services.includes(
                                        service.id
                                    ),
                                    ...classes,
                                }"
                            >
                                <input
                                    :id="service.id"
                                    v-model="services"
                                    v-ls-track.change="{
                                        target: 'ls_customer_leads-service',
                                        attachValue: true,
                                    }"
                                    type="checkbox"
                                    :value="service.id"
                                />
                                <span class="ls-button-toggle-icon">
                                    <branded-checkbox
                                        :is-active="services.includes(service.id)"
                                        :extra-classes="classes"
                                    />
                                </span>
                                <span>{{ service.name }}</span>
                            </label>
                        </div>
                    </ValidationProvider>
                    <a
                        v-if="!showAll"
                        v-ls-track.click="{
                            target: 'ls_customer_leads-view_complete_list',
                        }"
                        class="ls-customer-leads--show-all"
                        data-font-size="0"
                        @click.prevent="showAll = true"
                    >
                        <span>
                            View Complete List&nbsp;
                            <lsIconChevronRight />
                        </span>
                    </a>
                </div>
            </div>

            <div class="grid" test-id="details">
                <div class="ls-customer-leads--divider"></div>

                <div class="grid__col-12">
                    <h2 class="ls-condensed section-title">
                        Additional Details
                    </h2>
                </div>

                <div class="grid__col-sm-10">
                    <label class="ls-label ls-margin--top" for="notes"
                        >Tell Us About the Job<span class="ls-normal">
                            (more details will help the pro give an immediate price)</span
                        ></label
                    >
                    <div class="ls-input__wrapper" :class="{ 'ls-padding--right': !screenSize.xs }">
                        <ValidationProvider v-slot="{ classes }" ref="notes" rules="required">
                            <textarea
                                id="notes"
                                v-model="notes"
                                v-ls-track.change="{
                                    target: 'ls_customer_leads-notes',
                                    attachValue: true,
                                }"
                                class="ls-input"
                                :class="classes"
                                type="textbox"
                                rows="3"
                                test-id="notes"
                            ></textarea>
                        </ValidationProvider>
                    </div>

                    <ValidationProvider v-slot="{ classes }" ref="locations" rules="required">
                        <label class="ls-label ls-margin--top" for="location">Location</label>
                        <div class="ls-customer-leads--locations-wrapper ls-input__wrapper">
                            <label
                                v-for="location in locationOptions"
                                :key="location.value"
                                class="ls-checkbox"
                                :class="classes"
                            >
                                <input
                                    :id="location.value"
                                    v-model="locations"
                                    type="checkbox"
                                    :value="location.value"
                                />
                                <branded-checkbox
                                    :is-active="locations.includes(location.value)"
                                    :is-big-icon="true"
                                />
                                <span data-font-size="-1" class="ls-location-label">{{
                                    location.label
                                }}</span>
                            </label>
                        </div>
                    </ValidationProvider>

                    <label class="ls-label ls-margin--top" for="budget">Budget</label>
                    <div
                        class="ls-input__wrapper for-select"
                        :class="{ 'ls-margin--right': !screenSize.xs }"
                    >
                        <ValidationProvider v-slot="{ classes }" ref="budget" rules="required">
                            <select
                                id="budget"
                                v-model="budget"
                                v-ls-track.change="{
                                    target: 'ls_customer_leads-budget',
                                    attachValue: true,
                                }"
                                class="ls-input"
                                :class="classes"
                                test-id="budget"
                            >
                                <option :value="null" selected disabled>- Select -</option>
                                <option
                                    v-for="budgetOption in budgetOptions"
                                    :key="budgetOption.label"
                                    :value="budgetOption.value"
                                >
                                    {{ budgetOption.label }}
                                </option>
                            </select>
                        </ValidationProvider>
                    </div>

                    <ValidationProvider v-slot="{ classes }" ref="stage" rules="required">
                        <LsRadios
                            v-model="stage"
                            label="Stage"
                            :options="stageOptions"
                            track-id="ls_customer_leads-stages"
                            :classes="classes"
                        />
                    </ValidationProvider>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import _ from 'lodash';
import {
    CART__ADD_SERVICE_TO_CART,
    CART__GET_MOWING_IN_CART,
    CART__REMOVE_SERVICE_FROM_CART,
    CUSTOMER_LEADS__ENSURE_LOADED,
    CUSTOMER_LEADS__SAVE_FORM_DATA,
    ORDER__GET_PRICED_LAWNTREATMENT,
    ORDER__GET_PRICED_MOWING_OPTIONS,
    ORDER__UPDATE_INTENT,
    ORDER__UPDATE_ORDER,
} from '@/store/types';
import { CYCLE_WEEKLY, serviceIds, serviceType } from '@/constants';
import { resolveIntent } from '@/router/router.helpers';
import LsRadios from '@/components/LsRadios/LsRadios.vue';
import lsIconChevronRight from '@/components/general/icons/lsIconChevronRight.vue';
import brandedCheckbox from '@/components/original-mydot/branded-checkbox/BrandedCheckbox.vue';

const locationOptions = [
    {
        label: 'Full yard',
        value: 'location_full',
    },
    {
        label: 'Front-yard',
        value: 'location_front',
    },
    {
        label: 'Backyard',
        value: 'location_back',
    },
    {
        label: 'Sideyard',
        value: 'location_side',
    },
    {
        label: 'Driveway',
        value: 'location_driveway',
    },
    {
        label: 'Other',
        value: 'location_other',
    },
];

export default {
    name: 'LsCustomerLeads',
    components: {
        brandedCheckbox,
        LsRadios,
        lsIconChevronRight,
    },
    data() {
        return {
            // form
            serviceOptions: [],
            services: [],
            notes: '',
            locationOptions,
            locations: [],
            budgetOptions: [],
            budget: null,
            stageOptions: [],
            stage: null,
            showAll: false,
            serviceIds,
            address: {},
        };
    },
    computed: {
        ...mapState(['customerLeads', 'order', 'screenSize', 'address']),
        ...mapGetters({
            lawnTreatment: ORDER__GET_PRICED_LAWNTREATMENT,
            mowingInCart: CART__GET_MOWING_IN_CART,
            pricedMowingOptions: ORDER__GET_PRICED_MOWING_OPTIONS,
        }),
        isLoadingServices() {
            return this.customerLeads.loadingStatus.isLoading;
        },
        isStandAloneTreatment() {
            return serviceType.TREATMENT === this.order.intent;
        },
        filteredServices() {
            return this.customerLeads.form.serviceOptions.filter(
                (service) =>
                    ![
                        serviceIds.TREATMENT,
                        serviceIds.FERTILIZATION,
                        serviceIds.LEAF_REMOVAL,
                        serviceIds.BUSH_TRIMMING,
                        serviceIds.BED_WEEDING,
                        serviceIds.MOSQUITO,
                        serviceIds.AERATION,
                        serviceIds.AERATION_OVERSEEDING,
                    ].includes(service.id)
            );
        },
    },
    async created() {
        await this[CUSTOMER_LEADS__ENSURE_LOADED]();

        const {
            serviceOptions,
            services,
            notes,
            locations,
            budgetOptions,
            budget,
            stageOptions,
            stage,
            showAll,
        } = _.cloneDeep(this.customerLeads.form);
        this.serviceOptions = serviceOptions;
        this.services = services;
        this.notes = notes;
        this.locations = locations;
        this.budgetOptions = budgetOptions;
        this.budget = budget;
        this.stageOptions = stageOptions;
        this.stage = stage;
        this.showAll = showAll;

        // Used in signup-web redirect
        this.fname = _.get(this.order, 'fname');
        this.lname = _.get(this.order, 'lname');
        this.phone = _.get(this.order, 'phone');
        this.address = _.get(this.order, 'address');
    },
    destroyed() {
        this.saveFormData();
    },
    methods: {
        ...mapActions([
            CART__ADD_SERVICE_TO_CART,
            CART__REMOVE_SERVICE_FROM_CART,
            CUSTOMER_LEADS__ENSURE_LOADED,
            CUSTOMER_LEADS__SAVE_FORM_DATA,
            ORDER__UPDATE_INTENT,
            ORDER__UPDATE_ORDER,
        ]),
        saveFormData() {
            this[CUSTOMER_LEADS__SAVE_FORM_DATA]({
                services: this.services,
                notes: this.notes,
                locations: this.locations,
                budget: this.budget,
                stage: this.stage,
                showAll: this.showAll,
            });
        },
        async checkFormValidation() {
            return this.$refs.customerLeads.validate();
        },
        isTreatmentService() {
            return (
                this.lawnTreatment &&
                this.hasServices([serviceIds.TREATMENT, serviceIds.FERTILIZATION])
            );
        },
        hasServices(serviceId) {
            return this.serviceOptions.some(({ id }) => serviceId.includes(id));
        },
        resolveIntent,
        onLawnMowingClick() {
            if (this.isStandAloneTreatment) {
                this[CART__REMOVE_SERVICE_FROM_CART](this.lawnTreatment);
                const weeklyMowing = this.pricedMowingOptions.find(
                    ({ price: { cycle } }) => cycle === CYCLE_WEEKLY
                );

                this[CART__ADD_SERVICE_TO_CART](weeklyMowing);
                this[ORDER__UPDATE_ORDER]({
                    ...this.order,
                    type: serviceType.MOWING,
                });
            }

            const params = { intent: serviceType.MOWING };
            this.resolveIntent({ params }, this[ORDER__UPDATE_INTENT]);

            this.$emit('onLawnMowingClick');
        },
        redirectToSignupWeb(intent) {
            const address = `${this.address.street}, ${this.address.city}, ${this.address.state}, ${this.address.zip}, ${this.address.country}`;
            const path = `${process.env.VUE_APP_SIGNUP_WEB_URL}/cart/contact-info?intent=${intent}&phone=${this.phone}&name=${this.fname}+${this.lname}&address=${address}`;

            window.location.assign(path);
        },
    },
};
</script>

<style lang="scss">
.ls-customer-leads--container {
    .ls-input {
        resize: none;
    }

    .ls-customer-leads--services-outer-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;

        @include max-screen(breakpoint(xs, max)) {
            grid-template-columns: 1fr;
        }

        & > * {
            height: 100%;
        }

        .ls-button--block + .ls-button--block {
            margin: 0 !important;
        }
    }

    .ls-customer-leads--invalid {
        color: palette(red) !important;
    }

    .ls-customer-leads--divider {
        background-color: #d5dbd9;
        height: 1px;
        margin: 16px 10px;
        width: 100%;
    }

    .ls-checkbox {
        display: flex;
        margin-left: 0;
        margin-bottom: rem(-3);
        padding-right: rem(-3);
        user-select: none;

        i {
            padding-right: rem(0);
            margin-top: 3px;
            font-size: rem(0);
        }

        span:before {
            // override spacing used elsewhere since we're using fa checkboxes
            content: '';
        }

        input {
            display: none;
        }
    }

    .ls-customer-leads--show-all {
        color: palette(green, color-dodge);
        margin: 30px 0 5px 0;
        font-weight: 600;

        > span {
            display: flex;
            justify-content: center;
            align-items: center;

            > svg {
                margin-left: 4px;
                width: 14px;
                height: 14px;

                > polygon {
                    fill: palette(green, color-dodge);
                }
            }
        }
    }

    .ls-customer-leads--locations-wrapper {
        display: flex;
        flex-flow: row wrap;

        > label {
            flex: 50%;
            max-width: 200px;
        }
    }
}

.themed-app {
    .ls-customer-leads--invalid {
        color: $theme-color-red !important;
    }

    .ls-customer-leads--divider {
        background-color: $theme-color-gray;
    }

    .ls-customer-leads--show-all {
        color: $theme-color-primary;

        > span {
            > svg {
                > polygon {
                    fill: $theme-color-primary;
                }
            }
        }
    }
}

.themed-lawnlove {
    .ls-customer-leads--container {
        .ls-checkbox {
            i {
                margin: 0 rem(-5) 0 0;

                &::before {
                    top: 0;
                }
            }
        }

        .ls-radios {
            .ls-radios--container {
                margin-bottom: rem(-4);
            }
        }
    }
}

.themed-homegnome .ls-location-label {
    margin-top: 2px;
    margin-left: 7px;
}
</style>
